/*
 * Fermata pack selector
 * ----------------------
 * Vanilla JS, no dependencies. Self-inits per [data-fermata-pack-root], so more
 * than one instance on a page is safe: every querySelector is scoped to its root.
 *
 * It reads everything from a JSON <script type="application/json"> block the
 * Liquid snippet emits (unit price in cents, currency, variants with swatch
 * hexes, discount tiers, translated strings) so nothing about price, colours
 * or copy is hardcoded here beyond English fallbacks. Prices/savings shown on
 * the pack cards are DISPLAY ONLY: the real discount is applied by Shopify's
 * automatic cart discounts. This script only adds the right quantities. Tier
 * prices arrive pre-formatted with the shop money format (same as the price
 * block and the cart); Intl formatting is only the fallback.
 *
 * i18n: strings arrive from Liquid via `fermata.*` translation keys; any
 * missing key (value containing "Translation missing") falls back to the
 * English defaults in STR_DEFAULTS below. Tokens use [square_brackets].
 *
 * Colour UI: builds a swatch row from the JSON block and hides the <select>,
 * which stays the single source of truth for the chosen colour (the no-JS
 * fallback is a separate <noscript> form in the snippet). "One colour / Mix
 * colours" is a segmented toggle, hidden for the 1-strap pack where mixing is
 * meaningless. Mix mode renders one swatch row per strap (state in
 * this.mixValues; no selects), preserving row picks when the pack size
 * changes. Leaving mix mode brings the gallery back to the one-colour pick.
 *
 * Sold-out colours: in one-colour mode a sold-out swatch can be tapped to look
 * at it. That drives the native picker like any pick (photo, URL, and the
 * back-in-stock app's sign-up), but the colour is held in soldOutViewId, never
 * in the select, so it can never reach the cart; meanwhile both add buttons
 * say Sold out. Mix rows keep sold-out colours inert; a sold-out colour being
 * viewed survives mix mode and is shown again on return, like a pick.
 *
 * Colour-to-image swap: the product template renders with hide_variants, so the
 * server PRUNES every non-selected colour's slide from the gallery DOM. The
 * swap therefore drives the hidden native <variant-selects> the snippet
 * renders: its change event is what product-info.js listens for, and that code
 * re-fetches the section, reconciles the gallery, calls setActiveMedia and
 * syncs the zoom modal. See _swapGallery / _driveNativePicker.
 *
 * Hero-first: on a plain product URL the gallery snippet already renders the
 * first NON-variant image (the hero, i.e. whatever generic image comes first
 * in the product's media order) as the active slide. _showHeroFirst covers any
 * other load where a colour photo sits first without a matching pick. Colour
 * photos then appear only after an active pick. A ?variant= link (cart line,
 * shared link, reload after a pick) opens on that colour instead: available
 * colours are preselected, sold-out ones are shown as viewed.
 *
 * Sticky mobile bar: the section's own sticky add-to-cart targets the native
 * buy button, which this selector removes, so it never shows on Fermata.
 * _initStickyBar takes the bar over: shows it when the pack add button leaves
 * the viewport, routes its tap to the pack add flow, and keeps its price line
 * on the current pack total.
 *
 * Trust badge: the Giraffly app embed anchors its "checkout securely" strip
 * to the native product form; with the form gone it falls back to a slot above
 * this selector. _relocateTrustBadge moves it directly below the add button,
 * matching its position on every other product page. Retries because the app
 * injects asynchronously.
 *
 * Availability: every colour pick makes product-info.js fetch the section; the
 * fetched document contains this snippet's own JSON block with fresh
 * availability for every colour, so _subscribeVariantData refreshes the
 * swatches and dropdowns from it. No extra requests, no stale sold-out state.
 *
 * Cart-add follows the theme's existing convention (see cable-configurator.js
 * ~2027-2054): POST /cart/add.js with {items:[{id,quantity}, ...]}, then open
 * the drawer and dispatch arachne:cart:added + cart:refresh.
 */
(function () {
  "use strict";

  var STR_DEFAULTS = {
    choose_a_colour: "Choose a colour",
    sold_out_suffix: "(sold out)",
    most_popular: "Most popular",
    save_amount: "Save [amount]",
    add_pack_priced: "Add [pack] · [price]",
    strap_one: "1 strap",
    strap_count: "[count] straps",
    strap_n: "Strap [n]",
    all_in_colour: "All [count] straps in the same colour",
    your_pack: "Your pack",
    one_colour: "One colour",
    mix_colours: "Mix colours",
    error_choose_colour: "Please choose a colour.",
    adding: "Adding.",
    added: "✓ Added to cart",
    error_add: "Could not add to cart.",
    error_generic: "Something went wrong. Please try again."
  };

  function fmt(str, map) {
    return String(str).replace(/\[(\w+)\]/g, function (m, k) {
      return map && map[k] != null ? map[k] : m;
    });
  }

  function moneyFromCents(cents, currency) {
    var amount = (cents / 100);
    try {
      // Fallback only (see _tierMoney): used when the JSON block has no
      // shop-formatted prices, e.g. an older cached page. Storefront language,
      // not browser locale, so it at least follows the page language.
      return new Intl.NumberFormat(document.documentElement.lang || undefined, {
        style: "currency",
        currency: currency || "EUR"
      }).format(amount);
    } catch (e) {
      // Fallback: plain number with two decimals plus the raw currency code.
      var n = amount.toFixed(2);
      return (currency ? currency + " " : "") + n;
    }
  }

  // Shopify's `t` filter HTML-escapes translations (fr "d'ajouter" arrives as
  // "d&#39;ajouter") and a money format may use entities ("&euro;{{amount}}"),
  // while everything here is written with textContent, so decode once. A
  // <textarea> parses its content as text only: no elements are created and
  // nothing executes. Decoded strings must never go into innerHTML.
  function decodeEntities(s) {
    if (s.indexOf("&") === -1) return s;
    var ta = document.createElement("textarea");
    ta.innerHTML = s;
    return ta.value;
  }

  // Theme-wide button labels (window.variantStrings, layout/theme.liquid),
  // escaped by `t` like ours. Empty string when missing.
  function themeString(key) {
    var s = window.variantStrings && window.variantStrings[key];
    return typeof s === "string" && s ? decodeEntities(s) : "";
  }

  function numericId(v) {
    var n = parseInt(v, 10);
    return isNaN(n) ? v : n;
  }

  // Build a DOM node with attrs and children.
  function h(tag, attrs, kids) {
    var el = document.createElement(tag);
    if (attrs) {
      Object.keys(attrs).forEach(function (k) {
        if (k === "text") { el.textContent = attrs[k]; }
        else if (attrs[k] != null) { el.setAttribute(k, attrs[k]); }
      });
    }
    (kids || []).forEach(function (c) { if (c) el.appendChild(c); });
    return el;
  }

  function PackSelector(root) {
    this.root = root;
    this.submitting = false;
    root.classList.add("fermata-pack--js");

    var dataEl = root.querySelector("[data-fermata-pack-data]");
    var cfg = {};
    if (dataEl) {
      try { cfg = JSON.parse(dataEl.textContent); } catch (e) { cfg = {}; }
    }
    this.unitPrice = parseInt(cfg.unit_price, 10) || 0;
    this.currency = cfg.currency || "EUR";
    this.strings = this._mergeStrings(cfg.strings);
    this.variants = (cfg.variants || []).filter(function (v) { return v && v.id; });
    this.tiers = (cfg.tiers || []).slice().sort(function (a, b) { return a.qty - b.qty; });
    if (!this.tiers.length) this.tiers = [{ qty: 1, off: 0 }];

    // State
    this.packQty = this._defaultQty();
    this.mixMode = false;
    this.mixValues = []; // variant id per strap row in mix mode
    // Colour the URL asked for (?variant= from a cart line, a shared link, a
    // search result, or a reload after a pick). The server resolved it, so its
    // photo is the one the gallery shows. An available colour is preselected;
    // a sold-out one is shown as viewed (soldOutViewId), like a tap on it.
    var requested = this._variantById(cfg.selected_id);
    this.initialId = requested && requested.available ? String(requested.id) : "";
    // One-colour mode only: a sold-out colour the customer is looking at.
    // Never written into the select, so it can never reach the cart payload.
    this.soldOutViewId = requested && !requested.available ? String(requested.id) : "";
    // Colour the gallery and URL currently show (set by _swapGallery).
    this.galleryId = requested ? String(requested.id) : "";

    // Scoped element refs
    this.elCards = root.querySelector("[data-fermata-cards]");
    this.elSameWrap = root.querySelector("[data-fermata-same]");
    this.elSameSelect = root.querySelector("[data-fermata-same-select]");
    this.elSwatches = root.querySelector("[data-fermata-swatches]");
    this.elColourName = root.querySelector("[data-fermata-colour-name]");
    this.elSameNote = root.querySelector("[data-fermata-same-note]");
    this.elModeWrap = root.querySelector("[data-fermata-mode]");
    this.elModeBtns = root.querySelectorAll("[data-fermata-mode-btn]");
    this.elMixWrap = root.querySelector("[data-fermata-mix]");
    this.elMixRows = root.querySelector("[data-fermata-mix-rows]");
    this.elSummary = root.querySelector("[data-fermata-summary]");
    this.elSummaryDots = root.querySelector("[data-fermata-summary-dots]");
    this.elAddBtn = root.querySelector("[data-fermata-add]");
    this.elStatus = root.querySelector("[data-fermata-status]");

    this._buildCards();
    this._buildSamePicker();
    this._buildSwatches();
    this._wire();
    this._renderMixMode();
    this._syncQtyDependentUI();
    // A ?variant= colour with its own photo is already on screen; otherwise
    // make sure the hero shows first.
    if (!(requested && requested.media_id)) this._showHeroFirst();
    this._initStickyBar();
    this._relocateTrustBadge();
    this._subscribeVariantData();
  }

  PackSelector.prototype._mergeStrings = function (given) {
    var out = {};
    given = given || {};
    Object.keys(STR_DEFAULTS).forEach(function (k) {
      var v = given[k];
      if (typeof v === "string") v = decodeEntities(v);
      if (typeof v !== "string" || !v || v.toLowerCase().indexOf("translation missing") !== -1) {
        v = STR_DEFAULTS[k];
      }
      out[k] = v;
    });
    return out;
  };

  PackSelector.prototype._defaultQty = function () {
    // Single strap by default: the badge on the 3-pack does the upselling.
    for (var i = 0; i < this.tiers.length; i++) {
      if (this.tiers[i].qty === 1) return 1;
    }
    return this.tiers[0].qty;
  };

  PackSelector.prototype._tierFor = function (qty) {
    for (var i = 0; i < this.tiers.length; i++) {
      if (this.tiers[i].qty === qty) return this.tiers[i];
    }
    return null;
  };

  PackSelector.prototype._packPriceCents = function (qty) {
    var tier = this._tierFor(qty);
    var off = tier ? (tier.off || 0) : 0;
    return Math.round(this.unitPrice * qty * (1 - off));
  };

  // Shop-formatted price for a tier field (price_fmt / full_fmt / save_fmt),
  // falling back to Intl formatting of the computed cents when absent.
  PackSelector.prototype._tierMoney = function (qty, key, cents) {
    var tier = this._tierFor(qty);
    var s = tier ? tier[key] : null;
    if (typeof s === "string" && s) return decodeEntities(s);
    return moneyFromCents(cents, this.currency);
  };

  PackSelector.prototype._packLabel = function (qty) {
    return qty === 1 ? this.strings.strap_one : fmt(this.strings.strap_count, { count: qty });
  };

  PackSelector.prototype._buildCards = function () {
    if (!this.elCards) return;
    var self = this;
    this.elCards.innerHTML = "";
    this.tiers.forEach(function (tier) {
      var qty = tier.qty;
      var off = tier.off || 0;
      var packPrice = self._packPriceCents(qty);
      var fullPrice = self.unitPrice * qty;

      var kids = [];
      if (qty === 3) {
        kids.push(h("span", { class: "fermata-pack__badge", text: self.strings.most_popular }));
      }
      kids.push(h("span", { class: "fermata-pack__card-qty", text: self._packLabel(qty) }));
      var priceKids = [document.createTextNode(self._tierMoney(qty, "price_fmt", packPrice))];
      if (off > 0) {
        // Anchor: the undiscounted total, struck through next to the pack price.
        priceKids.push(h("s", { class: "fermata-pack__card-compare", text: self._tierMoney(qty, "full_fmt", fullPrice) }));
      }
      var priceLine = h("span", { class: "fermata-pack__card-price" });
      priceKids.forEach(function (n) { priceLine.appendChild(n); });
      kids.push(priceLine);
      if (off > 0) {
        var saved = self._tierMoney(qty, "save_fmt", fullPrice - packPrice);
        kids.push(h("span", { class: "fermata-pack__card-save", text: fmt(self.strings.save_amount, { amount: saved }) }));
      } else {
        kids.push(h("span", { class: "fermata-pack__card-save fermata-pack__card-save--none", text: " " }));
      }

      var card = h("button", {
        type: "button",
        class: "fermata-pack__card",
        "data-fermata-card": qty,
        "aria-pressed": qty === self.packQty ? "true" : "false"
      }, kids);
      if (qty === self.packQty) card.classList.add("is-selected");

      card.addEventListener("click", function () { self._selectQty(qty); });
      self.elCards.appendChild(card);
    });
  };

  PackSelector.prototype._colourOptions = function () {
    // <option> list of variant names; unavailable ones are disabled.
    var strs = this.strings;
    return this.variants.map(function (v) {
      return h("option", {
        value: v.id,
        text: v.available ? v.name : v.name + " " + strs.sold_out_suffix,
        disabled: v.available ? null : "disabled"
      });
    });
  };

  PackSelector.prototype._firstAvailableId = function () {
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].available) return String(this.variants[i].id);
    }
    return this.variants.length ? String(this.variants[0].id) : "";
  };

  PackSelector.prototype._variantById = function (variantId) {
    if (!variantId) return null;
    for (var i = 0; i < this.variants.length; i++) {
      if (String(this.variants[i].id) === String(variantId)) return this.variants[i];
    }
    return null;
  };

  // On load, show the first NON-variant image (the hero) instead of the theme's
  // default, which is the first variant's featured photo. Waits for the
  // media-gallery element definition because deferred script order puts this
  // file before media-gallery.js in the DOM, so on fast loads the custom
  // element may not be upgraded yet when the selector initialises.
  PackSelector.prototype._showHeroFirst = function () {
    var self = this;
    try {
      if (!window.customElements || !customElements.whenDefined) return;
      customElements.whenDefined("media-gallery").then(function () {
        try {
          var info = self.root.closest("product-info");
          var gallery = (info || document).querySelector("media-gallery");
          if (!gallery || typeof gallery.setActiveMedia !== "function") return;
          var viewer = gallery.querySelector('[id^="GalleryViewer"]');
          if (!viewer) return;
          var variantMedia = {};
          for (var i = 0; i < self.variants.length; i++) {
            if (self.variants[i].media_id) variantMedia[String(self.variants[i].media_id)] = true;
          }
          var slides = viewer.querySelectorAll("[data-media-id]");
          for (var s = 0; s < slides.length; s++) {
            var full = slides[s].getAttribute("data-media-id") || "";
            var suffix = full.substring(full.lastIndexOf("-") + 1);
            if (variantMedia[suffix]) continue;
            // First non-colour slide found: this is the hero.
            if (slides[s].classList.contains("is-active")) return;
            gallery.setActiveMedia(full, true);
            return;
          }
        } catch (e) { /* non-fatal */ }
      });
    } catch (e) { /* non-fatal */ }
  };

  // Take over the section's sticky mobile add-to-cart bar. Its own script
  // targets the native buy button this selector removes, so it never shows on
  // Fermata. We show it when the pack add button leaves the viewport, route
  // its tap to the pack add flow, and keep the price line on the pack total.
  PackSelector.prototype._initStickyBar = function () {
    try {
      var self = this;
      var info = this.root.closest("product-info");
      var sectionId = info && info.dataset ? info.dataset.section : "";
      var bar = document.getElementById("StickyATC-" + sectionId) || document.querySelector(".sticky-atc");
      if (!bar || !this.elAddBtn) return;
      var btn = bar.querySelector(".sticky-atc__btn");
      if (!btn || !btn.parentNode) return;

      // Replace the button node to drop the theme's own click handler, which
      // would scroll instead of adding.
      var fresh = btn.cloneNode(true);
      btn.parentNode.replaceChild(fresh, btn);
      this.elStickyBtn = fresh;
      this.stickyAddLabel = (fresh.textContent || "").trim();
      this.elStickyBar = bar;
      this.elStickyPrice = bar.querySelector("[data-sticky-price]");

      // The section may render the button disabled from the first variant's
      // availability; the pack flow handles availability per colour.
      this._syncStickyBtnAvailability();

      fresh.addEventListener("click", function () {
        if (fresh.disabled || self._isSoldOutState()) return;
        var items = self._buildItems();
        if (!items.length) {
          // Nothing picked yet: bring the selector into view instead of
          // erroring off-screen.
          self._setStatus(self.strings.error_choose_colour, true);
          self.root.scrollIntoView({ behavior: "smooth", block: "center" });
          return;
        }
        self.addToCart();
      });

      var ticking = false;
      var visible = false;
      var update = function () {
        ticking = false;
        var rect = self.elAddBtn.getBoundingClientRect();
        var show = rect.bottom < 0 || rect.top > window.innerHeight;
        if (show === visible) return;
        visible = show;
        bar.setAttribute("aria-hidden", show ? "false" : "true");
        bar.classList.toggle("is-visible", show);
        // Keep the hidden bar's button out of the tab order: aria-hidden
        // without tabindex management leaves a focusable ghost off-screen.
        fresh.tabIndex = show ? 0 : -1;
      };
      fresh.tabIndex = -1;
      window.addEventListener("scroll", function () {
        if (ticking) return;
        ticking = true;
        requestAnimationFrame(update);
      }, { passive: true });
      window.setTimeout(update, 100);

      // Keep the bar's price line showing the current pack total; the section's
      // own observer writes the single-variant price on colour changes, and our
      // observer registers later so our write runs after theirs and wins.
      this._syncStickyPrice();
      var priceContainer = document.getElementById("price-" + sectionId);
      if (priceContainer && window.MutationObserver) {
        new MutationObserver(function () { self._syncStickyPrice(); })
          .observe(priceContainer, { childList: true, subtree: true, characterData: true });
      }
    } catch (e) { /* non-fatal */ }
  };

  // Move the Giraffly trust-badge strip ("checkout securely with…") from its
  // fallback slot above the selector to directly below the add button, where
  // the app itself puts it on products that still have a native form. The app
  // injects asynchronously and its class name carries a per-shop hash, so:
  // match on the class prefix, climb to the direct child of the shared
  // container, and retry with backoff until it shows up. Guarded throughout;
  // if the app is removed this quietly does nothing.
  PackSelector.prototype._relocateTrustBadge = function () {
    var self = this;
    var tries = 0;
    var attempt = function () {
      tries++;
      try {
        var container = self.root.parentElement;
        if (container) {
          var badge = container.querySelector('[class*="trust-badge-"]');
          if (badge) {
            var host = badge;
            while (host.parentElement && host.parentElement !== container) host = host.parentElement;
            if (host !== self.root && (host.compareDocumentPosition(self.root) & Node.DOCUMENT_POSITION_FOLLOWING)) {
              self.root.insertAdjacentElement("afterend", host);
            }
            return;
          }
        }
      } catch (e) { /* non-fatal */ return; }
      if (tries < 8) window.setTimeout(attempt, 300 * tries);
    };
    attempt();
  };

  // After every colour pick, product-info.js fetches the section and publishes
  // variantChange with the parsed document. That document contains this
  // snippet's own JSON block with fresh availability for every colour, so we
  // refresh the swatches and dropdowns from it: no extra requests, no stale
  // sold-out state.
  PackSelector.prototype._subscribeVariantData = function () {
    try {
      // PUB_SUB_EVENTS is a top-level const in pubsub.js — a script-scoped
      // binding, NOT a window property. window.PUB_SUB_EVENTS is always
      // undefined, which silently disabled this whole refresh path.
      if (typeof subscribe !== "function" || typeof PUB_SUB_EVENTS === "undefined" || !PUB_SUB_EVENTS.variantChange) return;
      var self = this;
      subscribe(PUB_SUB_EVENTS.variantChange, function (payload) {
        try {
          var html = payload && payload.data ? payload.data.html : null;
          var dataEl = html ? html.querySelector("[data-fermata-pack-data]") : null;
          if (!dataEl) return;
          var cfg = JSON.parse(dataEl.textContent);
          var fresh = (cfg && cfg.variants) || [];
          var byId = {};
          for (var i = 0; i < fresh.length; i++) byId[String(fresh[i].id)] = fresh[i];
          var changed = false;
          for (var j = 0; j < self.variants.length; j++) {
            var f = byId[String(self.variants[j].id)];
            if (f && f.available !== self.variants[j].available) {
              self.variants[j].available = f.available;
              changed = true;
            }
          }
          if (changed) self._refreshOptionLists();
        } catch (e) { /* non-fatal */ }
      });
    } catch (e) { /* non-fatal */ }
  };

  // Update option disabled state, labels and swatch states in place so open
  // dropdowns and current selections are preserved.
  PackSelector.prototype._refreshOptionLists = function () {
    var self = this;
    var selects = this.root.querySelectorAll("[data-fermata-same-select]");
    for (var s = 0; s < selects.length; s++) {
      var opts = selects[s].options;
      for (var o = 0; o < opts.length; o++) {
        var v = self._variantById(opts[o].value);
        if (!v) continue;
        opts[o].disabled = !v.available;
        opts[o].textContent = v.available ? v.name : v.name + " " + self.strings.sold_out_suffix;
      }
    }
    var swatches = this.root.querySelectorAll("[data-fermata-swatch], [data-fermata-mix-swatch]");
    for (var w = 0; w < swatches.length; w++) {
      var id = swatches[w].getAttribute("data-fermata-swatch") || swatches[w].getAttribute("data-fermata-mix-swatch");
      var sv = this._variantById(id);
      if (!sv) continue;
      var label = sv.available ? sv.name : sv.name + " " + this.strings.sold_out_suffix;
      swatches[w].classList.toggle("is-disabled", !sv.available);
      swatches[w].title = label;
      if (swatches[w].hasAttribute("data-fermata-mix-swatch")) {
        swatches[w].setAttribute("aria-disabled", sv.available ? "false" : "true");
      } else {
        // One-colour swatches stay operable when sold out (view + restock).
        swatches[w].setAttribute("aria-label", label);
      }
    }
    // The colour being viewed as sold out came back in stock: make it the pick.
    var viewed = this._variantById(this.soldOutViewId);
    if (viewed && viewed.available && this.elSameSelect) {
      this.soldOutViewId = "";
      this.elSameSelect.value = String(viewed.id);
    }
    this._syncSwatchSelection();
    this._syncAddState();
  };

  PackSelector.prototype._syncStickyPrice = function () {
    if (!this.elStickyPrice) return;
    var packPrice = this._packPriceCents(this.packQty);
    this.elStickyPrice.textContent = this._packLabel(this.packQty) + " · " + this._tierMoney(this.packQty, "price_fmt", packPrice);
  };

  PackSelector.prototype._syncStickyBtnAvailability = function () {
    if (!this.elStickyBtn) return;
    var soldOut = this._isSoldOutState();
    this.elStickyBtn.disabled = soldOut || !!this.submitting;
    // Keep the bag icon, swap only the label text (and only when it changes,
    // so the server-rendered button is left untouched on load).
    var label = soldOut ? this._soldOutLabel()
      : (themeString("addToCart") || this.stickyAddLabel || "Add to cart");
    if ((this.elStickyBtn.textContent || "").trim() === label) return;
    var nodes = this.elStickyBtn.childNodes;
    for (var i = nodes.length - 1; i >= 0; i--) {
      if (nodes[i].nodeType === 3) this.elStickyBtn.removeChild(nodes[i]);
    }
    this.elStickyBtn.appendChild(document.createTextNode(label));
  };

  // Swap the product gallery to the picked colour's image.
  // Preferred path: drive the hidden native picker so the theme performs a real
  // variant change (section re-fetch, gallery reconcile, setActiveMedia, zoom
  // modal sync). Required because hide_variants means the target slide usually
  // does not exist in the DOM until the server sends it. Falls back to direct
  // activation for slides that are already present. Guarded so nothing here can
  // ever break colour selection or add-to-cart.
  PackSelector.prototype._swapGallery = function (variantId) {
    try {
      var v = this._variantById(variantId);
      if (!v) return;
      this.galleryId = String(v.id);
      if (this._driveNativePicker(v)) return;
      this._activateLocalSlide(v);
    } catch (e) { /* non-fatal */ }
  };

  // Set the hidden native <variant-selects> to this variant's option value and
  // dispatch the change event product-info.js listens for. Returns true when
  // the picker took the request or already sits on this colour. Handles both
  // the select markup the fermata snippet renders and radio pickers, in case
  // the native picker block is ever re-enabled for this product.
  // Note: matching is by variant title, which for this single-option product IS
  // the colour value. A second option would make titles "A / B" and matching
  // would fail closed (no dispatch, local fallback only).
  PackSelector.prototype._driveNativePicker = function (v) {
    var info = this.root.closest("product-info");
    var picker = info ? info.querySelector("variant-selects") : null;
    if (!picker) return false;
    var name = String(v.name);

    var radios = picker.querySelectorAll('input[type="radio"]');
    for (var r = 0; r < radios.length; r++) {
      if (radios[r].value !== name) continue;
      if (radios[r].checked) {
        // Same colour re-picked: its slide is the one currently rendered.
        this._activateLocalSlide(v);
        return true;
      }
      radios[r].checked = true;
      radios[r].dispatchEvent(new Event("change", { bubbles: true }));
      return true;
    }

    var selects = picker.querySelectorAll("select");
    for (var s = 0; s < selects.length; s++) {
      var opts = selects[s].options;
      for (var o = 0; o < opts.length; o++) {
        if (opts[o].value !== name) continue;
        if (selects[s].value === name && opts[o].hasAttribute("selected")) {
          this._activateLocalSlide(v);
          return true;
        }
        // variant-selects reads the selected ATTRIBUTE, not just the property,
        // so move it before dispatching.
        for (var q = 0; q < opts.length; q++) opts[q].removeAttribute("selected");
        opts[o].setAttribute("selected", "selected");
        selects[s].value = name;
        selects[s].dispatchEvent(new Event("change", { bubbles: true }));
        return true;
      }
    }
    return false;
  };

  // Direct activation for a slide that already exists in the DOM (the currently
  // rendered variant). Also the last resort when no native picker is present.
  // The id must be the section-prefixed form the gallery uses in the DOM
  // ("<sectionId>-<mediaId>"), which we read off the matching slide.
  PackSelector.prototype._activateLocalSlide = function (v) {
    if (!v || !v.media_id) return;
    var mid = String(v.media_id);
    var info = this.root.closest("product-info");
    var gallery = (info || document).querySelector("media-gallery");
    if (!gallery || typeof gallery.setActiveMedia !== "function") return;
    var slide = gallery.querySelector('[data-media-id$="-' + mid + '"]');
    if (!slide) return;
    gallery.setActiveMedia(slide.getAttribute("data-media-id"), true);
  };

  PackSelector.prototype._buildSamePicker = function () {
    if (!this.elSameSelect) return;
    this.elSameSelect.innerHTML = "";
    var self = this;
    // Placeholder so no colour is pre-selected: the hero images show first,
    // and the gallery only swaps once the customer actively picks a colour.
    this.elSameSelect.appendChild(h("option", { value: "", text: this.strings.choose_a_colour }));
    this._colourOptions().forEach(function (o) { self.elSameSelect.appendChild(o); });
    // A colour-specific link (?variant=) is an explicit pick: preselect it.
    this.elSameSelect.value = this.initialId || "";
  };

  // Visual swatch row that fronts the hidden same-colour <select>. The select
  // stays the source of truth: a swatch click writes its value and dispatches
  // change, so the gallery swap and cart payload paths stay identical.
  PackSelector.prototype._buildSwatches = function () {
    if (!this.elSwatches) return;
    var self = this;
    this.elSwatches.innerHTML = "";
    this.variants.forEach(function (v) {
      var label = v.available ? v.name : v.name + " " + self.strings.sold_out_suffix;
      // Sold-out swatches stay operable (no aria-disabled): a tap shows the
      // colour and the restock sign-up; the label says it is sold out.
      var b = h("button", {
        type: "button",
        class: "fermata-pack__swatch" + (v.available ? "" : " is-disabled"),
        "data-fermata-swatch": v.id,
        "aria-label": label,
        title: label,
        style: "background-color:" + (v.swatch || "#888888") + ";"
      });
      b.addEventListener("click", function () {
        if (!self.elSameSelect) return;
        if (!v.available) { self._viewSoldOut(v); return; }
        self.soldOutViewId = "";
        self.elSameSelect.value = String(v.id);
        self.elSameSelect.dispatchEvent(new Event("change", { bubbles: false }));
      });
      self.elSwatches.appendChild(b);
    });
    this._syncSwatchSelection();
  };

  PackSelector.prototype._syncSwatchSelection = function () {
    var current = this.soldOutViewId || (this.elSameSelect ? String(this.elSameSelect.value) : "");
    var swatches = this.elSwatches ? this.elSwatches.querySelectorAll("[data-fermata-swatch]") : [];
    for (var i = 0; i < swatches.length; i++) {
      var isSel = swatches[i].getAttribute("data-fermata-swatch") === current && current !== "";
      swatches[i].classList.toggle("is-selected", isSel);
      swatches[i].setAttribute("aria-pressed", isSel ? "true" : "false");
    }
    if (this.elColourName) {
      var v = this._variantById(current);
      this.elColourName.textContent = v ? (v.available ? v.name : v.name + " " + this.strings.sold_out_suffix) : "";
    }
  };

  // Sold-out colour tapped in one-colour mode: show its name and photo and move
  // the hidden native picker onto it (that is what lets the back-in-stock app
  // offer its sign-up, as on other product pages). The select is cleared, so
  // nothing can be added until an available colour is picked.
  PackSelector.prototype._viewSoldOut = function (v) {
    this.soldOutViewId = String(v.id);
    if (this.elSameSelect) this.elSameSelect.value = "";
    this._syncSwatchSelection();
    if (!this.submitting) this._setStatus("", false);
    this._syncAddState();
    this._swapGallery(String(v.id));
  };

  PackSelector.prototype._anyAvailable = function () {
    for (var i = 0; i < this.variants.length; i++) {
      if (this.variants[i].available) return true;
    }
    return false;
  };

  PackSelector.prototype._soldOutLabel = function () {
    return themeString("soldOut") || "Sold out";
  };

  // True while the add buttons must say Sold out: a sold-out colour is being
  // viewed in one-colour mode, or no colour is available at all.
  PackSelector.prototype._isSoldOutState = function () {
    return (!this.mixMode && !!this.soldOutViewId) || !this._anyAvailable();
  };

  // Main add button label + disabled state, one place for every caller; then
  // the sticky bar button follows.
  PackSelector.prototype._syncAddState = function () {
    if (this.elAddBtn) {
      if (this._isSoldOutState()) {
        this.elAddBtn.textContent = this._soldOutLabel();
        this.elAddBtn.disabled = true;
      } else {
        this.elAddBtn.textContent = fmt(this.strings.add_pack_priced, {
          pack: this._packLabel(this.packQty),
          price: this._tierMoney(this.packQty, "price_fmt", this._packPriceCents(this.packQty))
        });
        this.elAddBtn.disabled = !!this.submitting;
      }
    }
    this._syncStickyBtnAvailability();
  };

  // One swatch row per strap — same design language as the one-colour picker.
  // Row choices live in this.mixValues and survive pack-size changes; a row
  // whose colour went unavailable reseeds from the one-colour pick (when
  // available) or the first available colour.
  PackSelector.prototype._buildMixRows = function () {
    if (!this.elMixRows) return;
    this.elMixRows.innerHTML = "";
    var chosen = this.elSameSelect ? this._variantById(this.elSameSelect.value) : null;
    var seed = (chosen && chosen.available) ? String(chosen.id) : this._firstAvailableId();
    var prev = this.mixValues || [];
    this.mixValues = [];
    for (var i = 0; i < this.packQty; i++) {
      var kept = this._variantById(prev[i]);
      this.mixValues.push(kept && kept.available ? String(prev[i]) : seed);
    }
    for (var r = 0; r < this.packQty; r++) {
      this.elMixRows.appendChild(this._buildMixRow(r));
      this._syncMixRow(r);
    }
  };

  PackSelector.prototype._buildMixRow = function (index) {
    var self = this;
    var rowLabel = fmt(this.strings.strap_n, { n: index + 1 });
    var head = h("div", { class: "fermata-pack__mix-head" }, [
      h("span", { class: "fermata-pack__mix-index", text: rowLabel }),
      h("span", { class: "fermata-pack__mix-name", "data-fermata-mix-name": index })
    ]);
    var swatchWrap = h("div", {
      class: "fermata-pack__swatches fermata-pack__swatches--mix",
      role: "group",
      "aria-label": rowLabel
    });
    this.variants.forEach(function (v) {
      var label = v.available ? v.name : v.name + " " + self.strings.sold_out_suffix;
      var b = h("button", {
        type: "button",
        class: "fermata-pack__swatch fermata-pack__swatch--sm" + (v.available ? "" : " is-disabled"),
        "data-fermata-mix-swatch": v.id,
        "data-fermata-mix-row": index,
        "aria-label": rowLabel + ": " + label,
        "aria-disabled": v.available ? "false" : "true",
        title: label,
        style: "background-color:" + (v.swatch || "#888888") + ";"
      });
      b.addEventListener("click", function () {
        if (!v.available) return;
        // A sold-out colour being viewed (soldOutViewId) is kept, like a
        // one-colour pick: leaving mix mode brings it back (_resyncGallery),
        // so its swatch ring and name never contradict the state.
        self.mixValues[index] = String(v.id);
        self._syncMixRow(index);
        self._swapGallery(String(v.id));
        if (!self.submitting) self._setStatus("", false);
      });
      swatchWrap.appendChild(b);
    });
    return h("div", { class: "fermata-pack__mix-row" }, [head, swatchWrap]);
  };

  PackSelector.prototype._syncMixRow = function (index) {
    if (!this.elMixRows) return;
    var current = String(this.mixValues[index] || "");
    var swatches = this.elMixRows.querySelectorAll('[data-fermata-mix-row="' + index + '"]');
    for (var i = 0; i < swatches.length; i++) {
      var isSel = swatches[i].getAttribute("data-fermata-mix-swatch") === current && current !== "";
      swatches[i].classList.toggle("is-selected", isSel);
      swatches[i].setAttribute("aria-pressed", isSel ? "true" : "false");
    }
    var nameEl = this.elMixRows.querySelector('[data-fermata-mix-name="' + index + '"]');
    if (nameEl) {
      var v = this._variantById(current);
      nameEl.textContent = v ? v.name : "";
    }
    this._syncSummary();
  };

  // "Your pack" dot row above the add button: one dot per strap in the pack's
  // chosen colours. Mix mode only — in one-colour mode the swatch ring and
  // helper note already say everything.
  PackSelector.prototype._syncSummary = function () {
    if (!this.elSummary || !this.elSummaryDots) return;
    if (!this.mixMode || this.packQty < 2) {
      this.elSummary.style.display = "none";
      return;
    }
    this.elSummaryDots.innerHTML = "";
    for (var i = 0; i < this.packQty; i++) {
      var v = this._variantById(this.mixValues[i]);
      this.elSummaryDots.appendChild(h("span", {
        class: "fermata-pack__summary-dot",
        title: v ? v.name : "",
        style: "background-color:" + ((v && v.swatch) || "#888888") + ";"
      }));
    }
    this.elSummary.style.display = "";
  };

  PackSelector.prototype._selectQty = function (qty) {
    this.packQty = qty;
    var cards = this.elCards ? this.elCards.querySelectorAll("[data-fermata-card]") : [];
    for (var i = 0; i < cards.length; i++) {
      var isSel = parseInt(cards[i].getAttribute("data-fermata-card"), 10) === qty;
      cards[i].classList.toggle("is-selected", isSel);
      cards[i].setAttribute("aria-pressed", isSel ? "true" : "false");
    }
    if (this.mixMode) this._buildMixRows();
    this._syncQtyDependentUI();
    this._setStatus("", false);
  };

  // Everything that depends on the pack size and colour mode: the mode toggle
  // (pointless for a single strap), the "all N straps in this colour" note,
  // the add button label and the sticky bar price.
  PackSelector.prototype._syncQtyDependentUI = function () {
    if (this.elModeWrap) this.elModeWrap.classList.toggle("is-hidden", this.packQty === 1);
    if (this.packQty === 1 && this.mixMode) {
      this.mixMode = false;
      this._renderMixMode();
      return; // _renderMixMode re-enters this sync
    }
    if (this.elSameNote) {
      this.elSameNote.textContent = (this.packQty > 1 && !this.mixMode)
        ? fmt(this.strings.all_in_colour, { count: this.packQty })
        : "";
    }
    this._syncAddState();
    this._syncSummary();
    this._syncStickyPrice();
  };

  PackSelector.prototype._renderMixMode = function () {
    if (this.elSameWrap) this.elSameWrap.style.display = this.mixMode ? "none" : "";
    if (this.elMixWrap) this.elMixWrap.style.display = this.mixMode ? "" : "none";
    for (var i = 0; i < this.elModeBtns.length; i++) {
      var isMixBtn = this.elModeBtns[i].getAttribute("data-fermata-mode-btn") === "mix";
      var sel = isMixBtn === this.mixMode;
      this.elModeBtns[i].classList.toggle("is-selected", sel);
      this.elModeBtns[i].setAttribute("aria-pressed", sel ? "true" : "false");
    }
    if (this.mixMode) this._buildMixRows();
    this._syncQtyDependentUI();
    if (!this.mixMode) this._resyncGallery();
  };

  // Back in one-colour mode, the gallery and URL may still show the last
  // mix-row pick. Return them to the one-colour pick (or the sold-out colour
  // being viewed), if there is one. No-op when they already match (initial
  // render, toggling without a mix pick), so nothing is re-fetched and the
  // page does not move.
  PackSelector.prototype._resyncGallery = function () {
    var id = this.soldOutViewId || (this.elSameSelect ? String(this.elSameSelect.value || "") : "");
    if (!id || id === this.galleryId) return;
    this._swapGallery(id);
  };

  PackSelector.prototype._wire = function () {
    var self = this;
    for (var i = 0; i < this.elModeBtns.length; i++) {
      (function (btn) {
        btn.addEventListener("click", function () {
          var wantMix = btn.getAttribute("data-fermata-mode-btn") === "mix";
          if (wantMix === self.mixMode) return;
          self.mixMode = wantMix;
          self._renderMixMode();
          self._setStatus("", false);
        });
      })(this.elModeBtns[i]);
    }
    if (this.elAddBtn) {
      this.elAddBtn.addEventListener("click", function () { self.addToCart(); });
    }
    if (this.elSameSelect) {
      this.elSameSelect.addEventListener("change", function () {
        if (self.elSameSelect.value) self.soldOutViewId = "";
        self._syncSwatchSelection();
        self._syncAddState();
        self._swapGallery(self.elSameSelect.value);
        // A new pick resolves "choose a colour" and makes an earlier
        // "added" or cart error stale. Leave "Adding." alone mid-request.
        if (!self.submitting) self._setStatus("", false);
      });
    }
  };

  // Collapse selected colours into {id: quantity} then into cart line items.
  PackSelector.prototype._buildItems = function () {
    var counts = {};
    if (this.mixMode) {
      for (var i = 0; i < this.packQty && i < this.mixValues.length; i++) {
        var id = this.mixValues[i];
        if (!id) continue;
        counts[id] = (counts[id] || 0) + 1;
      }
    } else {
      var one = this.elSameSelect ? this.elSameSelect.value : this._firstAvailableId();
      if (one) counts[one] = this.packQty;
    }
    return Object.keys(counts).map(function (id) {
      return { id: numericId(id), quantity: counts[id] };
    });
  };

  PackSelector.prototype._setStatus = function (msg, isError) {
    if (!this.elStatus) return;
    this.elStatus.textContent = msg || "";
    this.elStatus.classList.toggle("is-error", !!isError);
  };

  PackSelector.prototype._setSubmitting = function (on) {
    this.submitting = on;
    if (this.elAddBtn) {
      this.elAddBtn.disabled = on;
      this.elAddBtn.classList.toggle("is-loading", on);
    }
    if (!on) { this._syncAddState(); return; }
    if (this.elStickyBtn) this.elStickyBtn.disabled = true;
  };

  PackSelector.prototype.openDrawer = function () {
    // The pack selector does not own a drawer of its own. Nudge the host theme
    // to open/refresh whatever cart UI it has. Kept guarded so nothing here can
    // ever break the add-to-cart success path.
    try {
      var drawer = document.querySelector("cart-drawer");
      if (drawer && typeof drawer.open === "function") { drawer.open(); return; }
      if (drawer && drawer.classList) { drawer.classList.add("active", "animate", "is-open"); return; }
    } catch (e) { /* non-fatal */ }
  };

  PackSelector.prototype.addToCart = function () {
    var self = this;
    if (this.submitting) return;
    if (this._isSoldOutState()) return;

    var items = this._buildItems();
    if (!items.length) {
      this._setStatus(this.strings.error_choose_colour, true);
      return;
    }

    this._setSubmitting(true);
    this._setStatus(this.strings.adding, false);

    var body = { items: items };
    fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", "Accept": "application/json" },
      body: JSON.stringify(body)
    }).then(function (res) {
      // .catch on the parse: a 502/timeout HTML body must surface the
      // friendly error, not a raw JSON parser exception.
      return res.json().catch(function () { return null; }).then(function (json) { return { ok: res.ok, body: json }; });
    }).then(function (r) {
      if (!r.ok) {
        // Mark cart rejections: their text (Shopify's description or our
        // translated error_add) is meant for customers. Anything else that
        // lands in .catch (offline, DNS, CORS) carries browser wording like
        // "Failed to fetch" and gets the translated generic message instead.
        var cartErr = new Error((r.body && (r.body.description || r.body.message)) || self.strings.error_add);
        cartErr.fromCart = true;
        throw cartErr;
      }
      // Persistent success message in the existing aria-live status element:
      // on the live theme there is no cart drawer (cart_type "page"), so
      // without this the only add feedback is a header badge pulse —
      // invisible to screen readers and easy to miss on touch.
      self._setStatus(self.strings.added, false);
      self.openDrawer();
      document.dispatchEvent(new CustomEvent("arachne:cart:added", { detail: { items: body } }));
      try { document.dispatchEvent(new CustomEvent("cart:refresh")); } catch (e) {}
    }).catch(function (err) {
      self._setStatus(err && err.fromCart && err.message ? err.message : self.strings.error_generic, true);
    }).then(function () {
      self._setSubmitting(false);
    });
  };

  function initAll() {
    var roots = document.querySelectorAll("[data-fermata-pack-root]");
    for (var i = 0; i < roots.length; i++) {
      if (roots[i].__fermataPack) continue;
      roots[i].__fermataPack = new PackSelector(roots[i]);
    }
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", initAll);
  } else {
    initAll();
  }
})();
